import * as React from "react";
import { CircleCheck, CircleDashed, CircleMinus, CircleStop, CircleX } from "lucide-react";

import { cn } from "../lib/utils";
import { Icon, type IconComponent } from "./icon";
import { Throbber } from "./throbber";

/**
 * v2 Steps (redesign phase 5, #455): a vertical list of process steps with a
 * rail. One primitive for the web app's "Arbeitsschritte" (`ToolCallList`) and
 * the add-in's generation rail (`GenerationProgressPanel`). Storybook only
 * until implementation; see docs/design-library/04-implementation-notes.md.
 *
 * States (break #119). The icons are one family of rings:
 *   pending   CircleDashed  --ink-muted, label --ink-muted
 *   running   Throbber      --ink-muted, label --ink Semibold, aria-current="step"
 *   done      CircleCheck   --ink-muted (neutral: colour marks exceptions only)
 *   failed    CircleX       --destructive-strong
 *   skipped   CircleMinus   --ink-muted, label --ink-muted (also "found nothing")
 * A step is rendered exactly as the parent says: `running` only while the
 * parent knows it runs, so a finished turn never shows a stale spinner.
 *
 * Stopped (break #185): when the user stops a turn, the outcome is `stopped`,
 * not `failed`. `settleSteps` turns the steps that were still running or
 * pending into `skipped`, and the summary shows CircleStop in `--ink-muted`
 * (a neutral ring of the same family), never the red CircleX. The summary
 * text stays the app's ("Gestoppt").
 *
 * Rail: a 1px `--border` connector from one icon to the next, stopping 2px
 * short of each (`--divider` vanished between 28px rows on a light page, 1.2:1;
 * seen in Storybook). Decorative and never filled: progress is read from the
 * icons.
 * No animation besides the Throbber (which slows under reduced motion).
 *
 *   md  icon 16 in a 20 column, label Sora 14/20 (the add-in rail)
 *   sm  icon 12 in a 16 column, label Sora 12/16 (Arbeitsschritte in chat)
 *
 * A11y (break #120): an `<ol>`; each step's state is a visually hidden word
 * after its label ("läuft", from `labels`, or the step's `stateLabel`); the
 * first running step carries `aria-current="step"`. ONE polite live region per
 * group (`StepsStatus`) announces changes ("Dokumente lesen: fertig"), never a
 * region per step: the running Throbber is decorative. Where the list is split
 * (acts) or sits in a collapsed Disclosure (which unmounts its content), render
 * `StepsStatus` outside and pass `announce={false}`.
 */

export type StepState = "pending" | "running" | "done" | "failed" | "skipped";
export type StepsSize = "md" | "sm";
/** The turn's result as the parent knows it; drives the summary line. `stopped`: the user stopped it. */
export type StepsOutcome = "running" | "success" | "failed" | "stopped";
/** What a group's summary line shows: a step state, or `stopped` for a turn the user stopped. */
export type SummaryState = StepState | "stopped";

export interface StepItem {
  id: string;
  /** Wraps. The app picks the wording per state ("Quellen werden gesucht" / "Quellen gesucht"). */
  label: string;
  state: StepState;
  /** Right-aligned, e.g. "12 s" or "3 Treffer". */
  meta?: React.ReactNode;
  /** Always shown under the label; pass a `<Disclosure size="sm">` to collapse it. */
  detail?: React.ReactNode;
  /** Replaces the hidden state word for this step, e.g. "keine Ergebnisse". */
  stateLabel?: string;
}

/** The hidden state words. German defaults. */
export interface StepsLabels {
  pending: string;
  running: string;
  done: string;
  failed: string;
  skipped: string;
}

export const DEFAULT_STEPS_LABELS: StepsLabels = {
  pending: "ausstehend",
  running: "läuft",
  done: "fertig",
  failed: "fehlgeschlagen",
  skipped: "übersprungen",
};

const STATE_ICON: Record<Exclude<SummaryState, "running">, IconComponent> = {
  pending: CircleDashed,
  done: CircleCheck,
  failed: CircleX,
  skipped: CircleMinus,
  stopped: CircleStop,
};

/** The step glyph alone, for summary lines. Decorative: the text beside it carries the meaning. */
export function StepIcon({ state, size = 16, className }: { state: SummaryState; size?: 16 | 12; className?: string }) {
  return (
    <span data-slot="step-icon" data-state={state} aria-hidden="true" className={cn("inline-flex shrink-0", className)}>
      {state === "running" ? (
        <Throbber decorative size={size} className="text-ink-muted" />
      ) : (
        <Icon
          icon={STATE_ICON[state]}
          size={size}
          className={state === "failed" ? "text-destructive-strong" : "text-ink-muted"}
        />
      )}
    </span>
  );
}

// Without an outcome the summary shows the most significant step state.
const PRECEDENCE: StepState[] = ["failed", "running", "done", "pending", "skipped"];

/**
 * The state a group's summary line shows (break #121). With the turn's
 * `outcome` it follows the turn: red only for a failed turn, so a successful
 * turn with one failed step gets a neutral icon (the step stays red in the
 * list, and the summary text still counts it). Without an outcome: the worst
 * step state, failed > running > done > pending > skipped. A stopped turn is
 * `stopped`, never `failed` (break #185).
 */
export function stepsSummaryState(states: readonly StepState[], outcome?: StepsOutcome): SummaryState {
  if (outcome === "stopped") return "stopped";
  if (outcome === "running") return "running";
  if (outcome === "failed") return "failed";
  if (outcome === "success") return states.length > 0 && states.every((s) => s === "skipped") ? "skipped" : "done";
  return PRECEDENCE.find((s) => states.includes(s)) ?? "pending";
}

/**
 * The steps as a finished turn shows them. Under `stopped`, steps still
 * `running` or `pending` become `skipped` (their per-state `stateLabel` was
 * written for the old state, so it goes); every other outcome leaves them as
 * the parent said.
 */
export function settleSteps(steps: readonly StepItem[], outcome?: StepsOutcome): readonly StepItem[] {
  if (outcome !== "stopped") return steps;
  return steps.map((step): StepItem =>
    step.state === "running" || step.state === "pending" ? { ...step, state: "skipped", stateLabel: undefined } : step,
  );
}

function stateWord(step: StepItem, labels: StepsLabels) {
  return step.stateLabel ?? labels[step.state];
}

export interface StepsStatusProps {
  steps: readonly StepItem[];
  labels?: Partial<StepsLabels>;
}

/**
 * The group's one polite live region: silent on mount, then "<label>: <state>"
 * for every step that appeared or changed state (several joined). Always
 * mounted, so the change is announced.
 */
export function StepsStatus({ steps, labels }: StepsStatusProps) {
  const words = { ...DEFAULT_STEPS_LABELS, ...labels };
  const [message, setMessage] = React.useState("");
  const previous = React.useRef<Map<string, StepState> | null>(null);
  const signature = steps.map((s) => `${s.id}\u0000${s.state}`).join("\u0001");

  React.useEffect(() => {
    const before = previous.current;
    previous.current = new Map(steps.map((s) => [s.id, s.state]));
    if (!before) return;
    const changes = steps.filter((s) => before.get(s.id) !== s.state).map((s) => `${s.label}: ${stateWord(s, words)}`);
    if (changes.length > 0) setMessage(changes.join(". "));
    // `signature` captures every id/state change; labels are read from the same render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);

  return (
    <span role="status" className="sr-only" data-slot="steps-status">
      {message}
    </span>
  );
}

export interface StepsProps extends Omit<React.OlHTMLAttributes<HTMLOListElement>, "children"> {
  steps: readonly StepItem[];
  size?: StepsSize;
  labels?: Partial<StepsLabels>;
  /** Render the group's live region. `false` when a `StepsStatus` sits outside. */
  announce?: boolean;
}

const Steps = React.forwardRef<HTMLOListElement, StepsProps>(function Steps(
  { steps, size = "md", labels, announce = true, className, ...props },
  ref,
) {
  const words = { ...DEFAULT_STEPS_LABELS, ...labels };
  const md = size === "md";
  const currentId = steps.find((s) => s.state === "running")?.id;

  return (
    <>
      {/* role="list": `list-none` drops list semantics in Safari. */}
      <ol ref={ref} role="list" data-size={size} className={cn("flex flex-col", className)} {...props}>
        {steps.map((step, i) => {
          const last = i === steps.length - 1;
          const quiet = step.state === "pending" || step.state === "skipped";
          return (
            <li
              key={step.id}
              data-state={step.state}
              aria-current={step.id === currentId ? "step" : undefined}
              className={cn("flex", md ? "gap-2" : "gap-1.5")}
            >
              <div aria-hidden="true" className={cn("flex shrink-0 flex-col items-center", md ? "w-5" : "w-4")}>
                <span className={cn("flex items-center justify-center", md ? "h-5" : "h-4")}>
                  <StepIcon state={step.state} size={md ? 16 : 12} />
                </span>
                {!last && <span data-slot="connector" className="w-px flex-1 bg-border" />}
              </div>
              <div className={cn("flex min-w-0 flex-1 flex-col gap-0.5", !last && "pb-2")}>
                <div className="flex items-baseline gap-2">
                  <span
                    className={cn(
                      "min-w-0 flex-1 font-sans break-words",
                      md ? "text-body" : "text-caption",
                      quiet ? "text-ink-muted" : "text-ink",
                      step.state === "running" && "font-semibold",
                    )}
                  >
                    {step.label}
                    <span className="sr-only">, {stateWord(step, words)}</span>
                  </span>
                  {step.meta != null && (
                    <span className="shrink-0 font-sans text-caption text-ink-muted tabular-nums">{step.meta}</span>
                  )}
                </div>
                {step.detail != null && <div className="font-body text-caption text-ink-muted">{step.detail}</div>}
              </div>
            </li>
          );
        })}
      </ol>
      {announce && <StepsStatus steps={steps} labels={labels} />}
    </>
  );
});

export { Steps };
